import { useTranslation } from "react-i18next"

import type { LocalAiLeadBudgetRequest } from "@/api/local-ai-lead-api"

import { Check, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatResetDate } from "@/features/fair-use/fair-use-utils"

import {
  formatLeadSek,
  leadRequestDisplayName,
  leadRequestSecondaryLine,
} from "../lead-requests-utils"

type LeadRequestsTableProps = {
  rows: LocalAiLeadBudgetRequest[]
  /** SEK one approved block adds — quoted on the approve button. */
  blockCapSek: number
  /** 0 disables approving: the lead's month allowance has nothing left. */
  approvableBlocks: number
  isResolving: boolean
  onApprove: (row: LocalAiLeadBudgetRequest) => void
  onDeny: (row: LocalAiLeadBudgetRequest) => void
}

/**
 * The lead's version of the admin fair-use usage table.
 *
 * Same row grammar — who asked, when, why, and the two answers — with every
 * column a lead has no authority over removed: no plan Select (plans are
 * platform/admin business), no top-up or withdraw menu (a lead may only
 * resolve a request that exists), no usage bar (the budget figures belong to
 * the admin's month view, and showing a colleague's spend here would leak more
 * than the decision needs).
 *
 * Approving is inline and one click, at the tenant's configured block size.
 * The lead never types an amount: the allowance is a SEK ceiling the backend
 * owns, and letting a lead pick blocks against a number they cannot see is how
 * you get a 409 for every second approval.
 */
export function LeadRequestsTable({
  rows,
  blockCapSek,
  approvableBlocks,
  isResolving,
  onApprove,
  onDeny,
}: LeadRequestsTableProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? i18n.language
  const amount = formatLeadSek(blockCapSek, locale)
  const canApprove = approvableBlocks > 0

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[220px]">
              {t("localAiLead.requests.table.user", { defaultValue: "User" })}
            </TableHead>
            <TableHead className="min-w-[280px]">
              {t("localAiLead.requests.table.motivation", { defaultValue: "Motivation" })}
            </TableHead>
            <TableHead className="min-w-[120px]">
              {t("localAiLead.requests.table.asked", { defaultValue: "Asked" })}
            </TableHead>
            <TableHead className="w-56 text-right">
              {t("localAiLead.requests.table.actions", { defaultValue: "Actions" })}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const displayName = leadRequestDisplayName(row)
            const secondary = leadRequestSecondaryLine(row)
            return (
              <TableRow key={row.id} className="bg-warning/10">
                <TableCell className="max-w-[320px] align-top">
                  <div className="min-w-0">
                    <div className="truncate font-semibold" title={row.user_id}>
                      {displayName}
                    </div>
                    {secondary && (
                      <div className="text-muted-foreground truncate text-xs">{secondary}</div>
                    )}
                  </div>
                </TableCell>
                <TableCell className="align-top">
                  {row.motivation ? (
                    <blockquote className="border-warning text-muted-foreground border-l-2 pl-2 text-xs italic">
                      {row.motivation}
                    </blockquote>
                  ) : (
                    <span className="text-muted-foreground text-xs">
                      {t("localAiLead.requests.table.noMotivation", {
                        defaultValue: "No motivation given",
                      })}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground align-top text-xs">
                  {formatResetDate(row.ts, locale)}
                </TableCell>
                <TableCell className="text-right align-top">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      data-testid="local-ai-lead-approve"
                      disabled={isResolving || !canApprove}
                      size="sm"
                      title={
                        canApprove
                          ? undefined
                          : t("localAiLead.requests.allowanceSpentHint", {
                              defaultValue:
                                "Your approval allowance for this month is used up. An administrator can still approve.",
                            })
                      }
                      onClick={() => onApprove(row)}
                    >
                      <Check className="mr-1 size-3.5" />
                      {t("localAiLead.requests.approve", {
                        amount,
                        defaultValue: "Approve +{{amount}} SEK",
                      })}
                    </Button>
                    <Button
                      className="text-destructive hover:text-destructive"
                      data-testid="local-ai-lead-deny"
                      disabled={isResolving}
                      size="sm"
                      variant="ghost"
                      onClick={() => onDeny(row)}
                    >
                      <X className="mr-1 size-3.5" />
                      {t("localAiLead.requests.deny", { defaultValue: "Deny" })}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
